import React from 'react';
import { Link } from 'react-router-dom';
import s from './LegalPage.module.css';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    num: '01',
    title: 'Movement Green\'s Role in the VEU Program',
    body: (
      <>
        <p>
          The Victorian Energy Upgrades (VEU) program is a Victorian Government initiative
          administered by the Essential Services Commission (ESC) of Victoria. Under the
          program, Accredited Persons (APs) — businesses accredited by the ESC — install
          eligible energy-efficient products in homes and businesses, create Victorian Energy
          Efficiency Certificates (VEECs), and surrender those certificates to energy
          retailers in exchange for a financial benefit that is passed on to the customer
          as a point-of-sale discount or rebate.
        </p>
        <p>
          Movement Green Pty Ltd is a VEU Accredited Person, accredited by the ESC. As an
          Accredited Person, we assess eligibility, carry out eligible installations (directly
          or through our qualified installers), create VEECs, and apply the resulting VEU
          discount to your invoice — allowing our customers to access government rebates
          seamlessly from assessment through to installation.
        </p>
      </>
    ),
  },
  {
    num: '02',
    title: 'Rebate & Savings Estimates',
    body: (
      <>
        <p>
          Any rebate figures, bill savings, or energy reduction estimates displayed on this
          website — whether in text, graphics, case studies, or calculator tools — are
          indicative only. Actual outcomes depend on a range of factors including:
        </p>
        <ul className={s.list}>
          <li>The specific product model installed and its rated efficiency.</li>
          <li>Your property type, size, location, and existing appliances.</li>
          <li>The current prevailing price of VEECs at the time of installation.</li>
          <li>Your actual energy usage patterns and tariff rates.</li>
          <li>Eligibility requirements set by the ESC, which are subject to change.</li>
        </ul>
        <p>
          Movement Green does not guarantee any particular rebate amount or energy saving.
          Rebate values are confirmed in your quote prior to installation and are subject to
          VEU program requirements and the VEEC market at the time of installation.
        </p>
      </>
    ),
  },
  {
    num: '03',
    title: 'No Financial or Professional Advice',
    body: (
      <p>
        Nothing on this website or in our communications constitutes financial, investment,
        tax, legal, or professional energy advice. Information is provided for general
        informational purposes only. You should obtain independent professional advice
        appropriate to your circumstances before making decisions based on information
        provided by Movement Green.
      </p>
    ),
  },
  {
    num: '04',
    title: 'Commercial Arrangements',
    body: (
      <p>
        As an Accredited Person, Movement Green creates VEECs from eligible upgrades and
        receives a commercial benefit from the sale of those certificates. This benefit funds
        the VEU discount passed on to you and does not affect your rights under the VEU
        program. Installations may be carried out by our own team or by qualified
        subcontracted installers working under our accreditation, all of whom must meet VEU
        scheme obligations and provide a professional standard of service.
      </p>
    ),
  },
  {
    num: '05',
    title: 'VEU Program Information',
    body: (
      <>
        <p>
          For authoritative and up-to-date information about the Victorian Energy Upgrades
          program — including current eligible products, rebate rates, and accredited
          persons — please refer directly to the official government sources:
        </p>
        <ul className={s.list}>
          <li>
            Essential Services Commission (ESC):{' '}
            <a href="https://www.esc.vic.gov.au/victorian-energy-upgrades" target="_blank" rel="noopener noreferrer">
              esc.vic.gov.au/victorian-energy-upgrades
            </a>
          </li>
          
        </ul>
        <p>
          Other than holding accreditation as an Accredited Person under the VEU program,
          Movement Green is not affiliated with, endorsed by, or acting as an agent of the
          Victorian Government, the ESC, or Sustainability Victoria.
        </p>
      </>
    ),
  },
  {
    num: '06',
    title: 'Accuracy of Website Content',
    body: (
      <p>
        We make reasonable efforts to keep information on this website accurate and current.
        However, VEU program rules, eligible product lists, and rebate structures change
        regularly. Movement Green does not warrant that any information on this website is
        current, complete, or free from error. You should verify eligibility and rebate
        details with our team, as confirmed in your written quote, prior to any installation.
      </p>
    ),
  },
  {
    num: '07',
    title: 'Consumer Rights',
    body: (
      <p>
        Nothing in this Disclosure limits or excludes any rights you may have under the
        Australian Consumer Law (Schedule 2 of the <em>Competition and Consumer Act 2010</em>
        (Cth)) or other applicable legislation, including statutory guarantees that cannot
        lawfully be excluded.
      </p>
    ),
  },
];

export default function DisclosurePage() {
  return (
    <main className="page-enter">
      <SEO
        title="Disclosure | Movement Green"
        description="Movement Green's VEU program disclosure — our role as a VEU Accredited Person, commercial arrangements, and rebate estimate limitations."
        noindex
      />

      {/* Hero */}
      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroBadge}>Disclosure</div>
          <h1 className={`disp-lg ${s.heroTitle}`}>
            Transparent About<br/><em>Our Role.</em>
          </h1>
          <p className={s.heroMeta}>Effective date: 30 September 2026 &nbsp;·&nbsp; Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia</p>
        </div>
      </section>

      {/* Body */}
      <section className={s.body}>
        <div className={s.bodyInner}>

          {/* Accreditation status notice */}
          <div className={s.notice}>
            <svg className={s.noticeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <div className={s.noticeTitle}>Movement Green — VEU Accredited Person</div>
              <p className={s.noticeBody}>
                Movement Green Pty Ltd is an Accredited Person (AP) under the Victorian Energy
                Upgrades program, accredited by the Essential Services Commission (ESC) of
                Victoria. We deliver eligible upgrades and apply VEU discounts directly —
                so our customers access the full benefits of the program with a single,
                accountable provider.
              </p>
            </div>
          </div>

          {/* Intro */}
          <div className={s.section}>
            <div className={s.sectionBody}>
              <p>
                This Disclosure page explains our relationship with the Victorian Energy Upgrades
                (VEU) program, our commercial arrangements, and important limitations you should
                be aware of when using our website or services.
              </p>
            </div>
          </div>

          <div className={s.divider}/>

          {SECTIONS.map((sec, i) => (
            <React.Fragment key={sec.num}>
              <div className={s.section}>
                <span className={s.sectionNum}>{sec.num}</span>
                <h2 className={s.sectionTitle}>{sec.title}</h2>
                <div className={s.sectionBody}>{sec.body}</div>
              </div>
              {i < SECTIONS.length - 1 && <div className={s.divider}/>}
            </React.Fragment>
          ))}

          <div className={s.divider}/>

          {/* Contact strip */}
          <div className={s.contact}>
            <div className={s.contactTitle}>Questions About This Disclosure?</div>
            <p className={s.contactBody}>
              If you have questions about our role, accreditation, or commercial
              arrangements, please contact us:
            </p>
            <a href="mailto:contact@movementgreen.com.au" className={s.contactEmail}>
              contact@movementgreen.com.au
            </a>
            <p className={s.contactBody} style={{ marginTop: 8 }}>
              Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia
            </p>
          </div>

          <p className="body-sm" style={{ textAlign: 'center' }}>
            Also see our <Link to="/privacy" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Privacy Policy</Link> and <Link to="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Terms of Use</Link>.
          </p>

        </div>
      </section>
    </main>
  );
}
